<template>
    <div class="space-y-4">
        <!-- Attachments List -->
        <div v-if="attachments.length">
            <div class="flex items-center justify-between mb-3">
                <div class="flex items-center space-x-2">
                    <span class="text-sm font-medium text-gray-700">Attachments</span>
                    <span class="text-sm text-gray-500">({{ attachments.length }})</span>
                </div>
                <button v-if="editable && !uploading" 
                        @click="uploading=true"
                        class="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add File
                </button>
            </div>
            
            <div class="space-y-2">
                <div v-for="(doc, docI) in attachments" 
                     v-bind:key="doc.id" 
                     class="group flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-150">
                    <a :href="`/assignments/${assignment.id}/download/${doc.id}`"
                       v-bind:title="doc.original"
                       class="flex items-center space-x-3 text-gray-700 hover:text-indigo-600"
                       @click="visited(doc.id)">
                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span class="text-sm" v-text="truncatedDoc(doc.original)"></span>
                    </a>
                    
                    <button v-if="editable" 
                            @click="removeAttachment(doc.id, docI)"
                            class="hidden group-hover:flex items-center text-sm text-red-500 hover:text-red-700"
                            title="Delete attachment">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        
        <!-- No Attachments State -->
        <div v-else>
            <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-medium text-gray-700">Attachments</span>
                <button v-if="editable && !uploading" 
                        @click="uploading=true"
                        class="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add File
                </button>
            </div>
            <div class="flex items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <div class="text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p class="mt-1 text-sm text-gray-500">No attachments yet</p>
                </div>
            </div>
        </div>

        <!-- File Upload Section -->
        <div v-if="uploading && editable" 
             class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50 flex items-center justify-center"
             @click.self="uploading = false">
            <div class="bg-white rounded-lg shadow-xl p-6 sm:max-w-lg w-full mx-4 transform transition-all">
                <div class="flex items-center justify-between mb-5">
                    <h3 class="text-lg font-medium text-gray-900">Upload File</h3>
                    <button @click="uploading = false" 
                            class="text-gray-400 hover:text-gray-500">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <file-upload 
                    name="attachments" 
                    class="w-full" 
                    @loaded="setAttachments">
                </file-upload>

                <div class="mt-5 flex justify-end space-x-3">
                    <button type="button"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            @click="uploading = false">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import FileUpload from './FileUpload.vue';
    
    export default {
        components: {FileUpload},
        props: ['assignment', 'upload', 'readOnly'],
        data() {
            return {
                attachments: this.assignment.attachments,
                newAttachment: '',
                uploading: this.upload || false,
                editable: !this.readOnly || false
            }
        },
        methods: {
            setAttachments(doc) {
                this.newAttachment = doc.file;
                this.addAttachment();
            },
            addAttachment() {
                let data = new FormData();
                data.append('attachment', this.newAttachment);
                axios.post(`/assignments/${this.assignment.id}/attachments`, data)
                    .then(({data}) => {
                        this.newAttachment = '';
                        this.attachments.push(data);
                        this.uploading = false;
                    });
            },
            removeAttachment(id, docI) {
                axios.delete(`/attachments/${id}`)
                    .then(({data}) => {
                        this.attachments.splice(docI, 1);
                    })
            },
            visited(id) {
                let current_url = window.location.href;
                history.replaceState({}, "", `/assignments/${this.assignment.id}/download/${id}`);
                history.replaceState({}, "", current_url);
            },
            truncatedDoc(name) {
                if (name.length <= 25)
                    return name;
                return name.slice(0, 25) + '...';
            }
        }
    }
</script>
